import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Plus, X } from 'lucide-react';
import { Card } from '@/components/ui';
import { useMatters } from '@/contexts/MatterContext';
import { ApiError } from '@/lib/api';
import {
  getMatterSkills,
  listLegalSkills,
  setMatterSkills,
  type LegalSkillSummary,
} from '@/lib/api-client';

// P2 Section 2.7: assign legal skills to a matter and order them by priority.
// Priority is the array order here (top = highest = priority 1).

interface Assigned {
  slug: string;
  name: string;
}

export function AdminMatterSkills() {
  const { matterId = '' } = useParams();
  const { matters } = useMatters();
  const matterName = matters.find((m) => m.id === matterId)?.name ?? matterId;

  const [all, setAll] = useState<LegalSkillSummary[]>([]);
  const [assigned, setAssigned] = useState<Assigned[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [skills, current] = await Promise.all([
        listLegalSkills().catch(() => [] as LegalSkillSummary[]),
        matterId ? getMatterSkills(matterId).catch(() => []) : Promise.resolve([]),
      ]);
      if (cancelled) {
        return;
      }
      setAll(skills);
      // current is already priority-ordered from the API.
      setAssigned(current.map((c) => ({ slug: c.slug, name: c.name })));
    })();
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  const assignedSlugs = new Set(assigned.map((a) => a.slug));
  const available = all.filter((s) => !assignedSlugs.has(s.slug));

  function add(skill: LegalSkillSummary): void {
    setAssigned((prev) => [...prev, { slug: skill.slug, name: skill.name }]);
  }
  function remove(slug: string): void {
    setAssigned((prev) => prev.filter((a) => a.slug !== slug));
  }
  function move(index: number, dir: -1 | 1): void {
    setAssigned((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) {
        return prev;
      }
      const a = next[index];
      const b = next[target];
      if (!a || !b) {
        return prev;
      }
      next[index] = b;
      next[target] = a;
      return next;
    });
  }

  async function save(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      // Priority = array position + 1 (top = 1 = highest priority).
      const payload = assigned.map((a, i) => ({ slug: a.slug, priority: i + 1 }));
      const res = await setMatterSkills(matterId, payload);
      setNotice(
        res.unknown.length > 0
          ? `Saved ${res.assigned}. Unknown slugs skipped: ${res.unknown.join(', ')}`
          : `Saved ${res.assigned} skill${res.assigned === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Matter skills</h1>
          <Link
            to="/matters"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Matters
          </Link>
        </div>
        <p className="text-sm text-stone-400">
          Skills assigned to <span className="text-stone-200">{matterName}</span>, in priority order
          (top wins on conflict). These are injected into every query for this matter.
        </p>

        <Card variant="default" padding="md" className="space-y-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Assigned ({assigned.length})
          </p>
          {assigned.length === 0 ? (
            <p className="text-sm text-stone-500">No skills assigned. Add some below.</p>
          ) : (
            <ol className="space-y-2">
              {assigned.map((a, i) => (
                <li
                  key={a.slug}
                  className="flex items-center gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2"
                >
                  <span className="w-5 shrink-0 text-center text-[11px] tabular-nums text-amber-500">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-stone-200">{a.name}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      className="rounded p-1 text-stone-500 hover:text-amber-400 disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === assigned.length - 1}
                      aria-label="Move down"
                      className="rounded p-1 text-stone-500 hover:text-amber-400 disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(a.slug)}
                      aria-label="Remove"
                      className="rounded p-1 text-stone-500 hover:text-red-400"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {error ? <p className="text-sm text-red-400">{error}</p> : null}
          {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Save assignment
            </button>
          </div>
        </Card>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Available skills ({available.length})
          </p>
          {available.length === 0 ? (
            <p className="text-sm text-stone-500">
              {all.length === 0 ? 'No skills loaded. Add some on the Legal skills page.' : 'All loaded skills are assigned.'}
            </p>
          ) : (
            <div className="space-y-2">
              {available.map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => add(s)}
                  className="flex w-full items-center gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2 text-left transition-colors hover:border-stone-700"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0 text-stone-500" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-stone-200">{s.name}</span>
                    <span className="block text-[10px] uppercase tracking-[0.15em] text-stone-500">
                      {[...s.jurisdictions, ...s.areasOfLaw].join(', ') || s.slug}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
